import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { CheckCircle, ArrowRight, AlertTriangle, Zap, Video, FileText, User, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORY_ICON = {
  Media: User, Marketing: Zap, Ops: FileText,
  amplify: Zap, recurring_amplify: Zap,
  shoot_day: Video, clip_retainer_starter: Video, clip_retainer_pro: Video, production_retainer_full: Video,
  blog_post: FileText, white_paper: FileText, social_copy_pack: FileText, content_retainer: FileText,
};

export default function ProposalSignPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const statusParam = params.get('status');
  const sessionId = params.get('session_id');
  const [proposal, setProposal] = useState(null);
  const [error, setError] = useState('');
  const [signerName, setSignerName] = useState('');
  const [signerTitle, setSignerTitle] = useState('');
  const [acceptDisclaimer, setAcceptDisclaimer] = useState(false);
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState('loading'); // loading | ready | already_signed | paid | error

  const load = () => {
    if (!token) { setState('error'); setError('No token in URL.'); return; }
    axios.get(`${API}/proposals/preview?token=${token}`)
      .then(r => {
        setProposal(r.data);
        setState(['signed','paid','executing','completed'].includes(r.data.status) ? 'already_signed' : 'ready');
      })
      .catch(e => { setState('error'); setError(e.response?.data?.detail || 'Invalid link'); });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  // After Stripe redirect, confirm payment
  useEffect(() => {
    if (statusParam === 'paid' && sessionId && token) {
      axios.post(`${API}/proposals/mark-paid`, { token, session_id: sessionId })
        .then(() => { setState('paid'); load(); })
        .catch(() => {});
    }
    // eslint-disable-next-line
  }, [statusParam, sessionId, token]);

  const sign = async () => {
    if (signerName.trim().length < 2) { toast.error('Type your full legal name.'); return; }
    if (!acceptDisclaimer) { toast.error('You must accept the disclaimer to sign.'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/proposals/sign`, {
        token, signer_name: signerName, signer_title: signerTitle, accept_disclaimer: true,
      });
      if (r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        setState('paid');
        load();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sign failed');
    } finally { setBusy(false); }
  };

  if (state === 'loading') return (
    <div className="min-h-screen bg-[#090E33] flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-[#FF4343]" />
    </div>
  );

  if (state === 'error') return (
    <div className="min-h-screen bg-[#090E33] flex items-center justify-center p-4">
      <div className="bg-[#111827] border border-[#FF4343]/30 p-6 max-w-md">
        <h2 className="font-display text-xl font-bold text-[#FF4343] mb-2">This proposal isn't accessible</h2>
        <p className="text-sm text-[#9CA3AF]">{error}</p>
      </div>
    </div>
  );

  if (state === 'paid' || (state === 'already_signed' && ['paid','executing','completed'].includes(proposal?.status))) return (
    <div className="min-h-screen bg-[#090E33] flex items-center justify-center p-4">
      <div className="bg-[#111827] border border-[#10B981]/30 p-8 max-w-md text-center" data-testid="proposal-paid">
        <CheckCircle className="w-14 h-14 text-[#10B981] mx-auto mb-3" />
        <h2 className="font-display text-2xl font-bold text-white mb-2">You're all set.</h2>
        <p className="text-sm text-[#9CA3AF] mb-4">
          <strong className="text-white">{proposal?.title}</strong> has been signed and paid.
          The Freddy Media team has been notified and will kick off work within 3 business days.
        </p>
        <p className="text-[10px] text-[#6B7280] font-mono">Signed by {proposal?.signed_by} · {proposal?.signed_at && new Date(proposal.signed_at).toLocaleString()}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#090E33] py-10 px-4" data-testid="proposal-sign-page">
      <Toaster position="top-right" theme="dark" richColors />
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <img src="https://customer-assets.emergentagent.com/job_campaign-board/artifacts/pflz8b2v_freddy-logo-symbol.png" alt="" className="h-10 mx-auto mb-3" />
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FF4343] mb-2">Freddy Media proposal</p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white">{proposal.title}</h1>
          <p className="text-sm text-[#9CA3AF] mt-2">Prepared for <span className="text-white font-semibold">{proposal.brand_name}</span></p>
        </div>

        {/* Description */}
        {proposal.description && (
          <div className="bg-[#111827] border border-white/10 p-5">
            <p className="text-sm text-[#9CA3AF] leading-relaxed whitespace-pre-wrap">{proposal.description}</p>
          </div>
        )}

        {/* Line items */}
        <div className="bg-[#111827] border border-white/10 divide-y divide-white/5" data-testid="proposal-line-items">
          {proposal.line_items.map((li) => {
            const Icon = CATEGORY_ICON[li.type] || CATEGORY_ICON[li.department] || FileText;
            return (
              <div key={li.key} className="p-4 flex items-start gap-3">
                <div className={`w-10 h-10 rounded flex items-center justify-center flex-shrink-0 ${li.department === 'Media' ? 'bg-[#FF4343]/10 text-[#FF4343]' : 'bg-[#10B981]/10 text-[#10B981]'}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-white">{li.label}</span>
                    <span className="text-[10px] font-mono uppercase text-[#6B7280] bg-white/5 px-1.5 py-0.5">{li.department}</span>
                    {li.billing === 'monthly' && <span className="text-[10px] font-mono uppercase text-yellow-400 bg-yellow-500/10 px-1.5 py-0.5">Monthly</span>}
                  </div>
                  {li.description && <p className="text-xs text-[#9CA3AF] mt-1 leading-relaxed">{li.description}</p>}
                  {li.quantity > 1 && <p className="text-[10px] text-[#6B7280] font-mono mt-1">Qty {li.quantity}</p>}
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="font-mono text-base font-bold text-white">${li.line_total_usd.toLocaleString(undefined,{minimumFractionDigits:2, maximumFractionDigits:2})}</p>
                  {li.billing === 'monthly' && <p className="text-[10px] text-[#6B7280]">/mo</p>}
                </div>
              </div>
            );
          })}
          <div className="p-4 bg-[#090E33]/50 space-y-1.5 font-mono text-sm">
            <div className="flex justify-between text-[#9CA3AF]"><span>One-time total</span><span>${proposal.one_time_total_usd.toLocaleString(undefined,{minimumFractionDigits:2, maximumFractionDigits:2})}</span></div>
            <div className="flex justify-between text-[#9CA3AF]"><span>Recurring / month</span><span>${proposal.monthly_total_usd.toLocaleString(undefined,{minimumFractionDigits:2, maximumFractionDigits:2})}</span></div>
            <div className="flex justify-between text-white font-bold text-base pt-2 border-t border-white/10">
              <span>Due today</span>
              <span className="text-[#FF4343]" data-testid="proposal-due-today">
                ${(proposal.one_time_total_usd + proposal.monthly_total_usd).toLocaleString(undefined,{minimumFractionDigits:2, maximumFractionDigits:2})}
              </span>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="bg-[#111827] border border-white/10 p-5" data-testid="proposal-disclaimer">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-yellow-400" />
            <h3 className="text-sm font-semibold text-white">Terms of engagement (please read)</h3>
          </div>
          <div className="text-xs text-[#9CA3AF] leading-relaxed whitespace-pre-wrap max-h-64 overflow-y-auto p-3 bg-[#090E33] border border-white/5">
            {proposal.disclaimer_text}
          </div>
          <p className="text-[10px] text-[#6B7280] font-mono mt-2">Version {proposal.disclaimer_version}</p>
        </div>

        {/* Sign form */}
        {state === 'already_signed' ? (
          <div className="bg-[#111827] border border-yellow-500/30 p-5 text-center">
            <p className="text-sm text-yellow-400 font-mono">This proposal was already signed. Waiting on payment.</p>
          </div>
        ) : (
          <div className="bg-[#111827] border border-white/10 p-5 space-y-4" data-testid="proposal-sign-form">
            <h3 className="text-sm font-semibold text-white">Sign to accept</h3>
            <div>
              <label className="text-[10px] font-mono uppercase text-[#6B7280] block mb-1">Your full legal name *</label>
              <input value={signerName} onChange={e => setSignerName(e.target.value)}
                placeholder="Jane Q. Doe"
                data-testid="signer-name"
                className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase text-[#6B7280] block mb-1">Your title / role</label>
              <input value={signerTitle} onChange={e => setSignerTitle(e.target.value)}
                placeholder="e.g. VP Marketing"
                data-testid="signer-title"
                className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]" />
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={acceptDisclaimer} onChange={e => setAcceptDisclaimer(e.target.checked)}
                data-testid="accept-disclaimer"
                className="mt-0.5 accent-[#FF4343]" />
              <span className="text-xs text-[#9CA3AF] leading-snug">
                I have read the terms above and, on behalf of <span className="text-white">{proposal.brand_name}</span>, I agree to be bound by them. I authorize Freddy Media to charge the card I provide on the next screen for the one-time total today and monthly for any recurring line items.
              </span>
            </label>
            <button onClick={sign} disabled={busy}
              data-testid="sign-and-checkout"
              className="w-full border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] py-3 text-sm font-semibold hover:bg-[#E03030] disabled:opacity-50 flex items-center justify-center gap-2">
              {busy ? 'Redirecting…' : <>Accept &amp; sign — continue to payment <ArrowRight className="w-4 h-4" /></>}
            </button>
            <p className="text-[10px] text-[#6B7280] text-center">
              Secure payment via Stripe. Your typed name above is your legal e-signature per ESIGN Act / UETA.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
